// Simplified data store functions for frontend2
// Based on frontend/src/stores/data.store.tsx

import ApiService from './api.service';

export type ChatThread = {
  thread_id: string;
  name: string;
  agent_type: string;
  created_by: string | null;  // null for agent-initiated conversations
  created_at: string;
  updated_at: string;
  shared_with: string[];
  is_owner: boolean;
};

export type ChatThreadMessage = {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  tool_call_id?: string;
  name?: string;
  tool_calls?: Array<{
    id: string;
    name: string;
    args: any;
  }>;
}

// Mirrors the backend Verbosity StrEnum (src/models/ai_session.py).
// high = full narration, all tool calls render; medium = brief narration;
// low = terse responses + background tool calls hidden.
export type Verbosity = 'high' | 'medium' | 'low';

// Mirrors the backend ReasoningEffort StrEnum (src/models/ai_session.py) —
// adaptive thinking effort. Only has an effect on models that support it;
// the backend silently ignores it for the rest.
export type ReasoningEffort = 'max' | 'high' | 'medium' | 'low';

// Per-thread settings persisted on the backend AiThread doc.
// Fields are optional: null/undefined means "not set" and the frontend
// falls back to the agent's default.
export type ChatThreadSettings = {
  verbosity?: Verbosity | null;
  model?: string | null;
  reasoning?: ReasoningEffort | null;
};

export type ChatThreadMessagesResult = {
  messages: ChatThreadMessage[];
  settings?: ChatThreadSettings | null;
};

export const getChatThreadMessages = async (threadId: string): Promise<ChatThreadMessagesResult> => {
  try {
    const result = (await ApiService.getInstance().request('GET', `/chat/threads/${threadId}/messages`)) as {
      thread_id: string;
      messages: ChatThreadMessage[];
      settings?: ChatThreadSettings | null;
    };
    return {
      messages: result.messages,
      settings: result.settings,
    };
  } catch (error: any) {
    // 404 is expected for new threads that don't exist yet
    // The thread will be created automatically when the first message is sent
    if (error?.code === 404) {
      return {
        messages: [],
      };
    }
    // Re-throw other errors
    throw error;
  }
};

export const updateChatThreadSettings = async (threadId: string, settings: ChatThreadSettings): Promise<void> => {
  await ApiService.getInstance().request('PATCH', `/chat/threads/${threadId}`, { settings });
};

export type ChatModel = {
  id: string;
  name: string;
  supportsReasoning: boolean;
};

export const getAvailableModels = async (): Promise<ChatModel[]> => {
  const result = (await ApiService.getInstance().request('GET', '/models')) as {
    data: Array<{ id: string; name?: string; supports_reasoning?: boolean }>;
  };
  return result.data.map((m) => ({
    id: m.id,
    name: m.name || m.id,
    supportsReasoning: m.supports_reasoning ?? false,
  }));
};

export const listChatThreads = async (agentType?: string, limit: number = 50, userId?: string): Promise<ChatThread[]> => {
  const params: Record<string, any> = { limit };
  if (agentType) {
    params.agent_type = agentType;
  }
  // Pass user_id parameter - defaults to current user if not provided
  // If userId is explicitly provided, use it; otherwise backend will default to current user
  if (userId !== undefined) {
    params.user_id = userId;
  }
  const result = (await ApiService.getInstance().request('GET', '/chat/threads', params)) as {
    threads: ChatThread[];
  };
  return result.threads;
};

// Simple cache invalidation - in a real app you'd use React Query
export const invalidateChatThreads = () => {
  // This is a no-op in the simplified version
  // In the full version, this would invalidate React Query cache
};

export type CompactThreadResult = {
  thread_id: string;
  messages: ChatThreadMessage[];
  compacted: boolean;
  original_message_count: number;
  new_message_count: number;
};

export const compactThread = async (threadId: string): Promise<CompactThreadResult> => {
  const result = await ApiService.getInstance().request('POST', `/chat/threads/${threadId}/compact`);
  return result as CompactThreadResult;
};

